import { useAtom, useAtomValue } from "jotai";
import styles from "../styles.module.scss";
import { ReactComponent as TrashIcon } from "../../../assets/trash.svg";
import { pageIndexAtom, userAtom } from "../../../utils/store";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Spin } from "antd";
import {
  createReference,
  deleteReference,
  getAllTitle,
  getReferenceByApplicantId,
} from "../../../utils/request";
import ReferenceForm from "./form";
import OnboardingButtonGroup from "../onboardingButtonGroup";

const REFEREE_CATEGORIES = [
  { id: 0, name: "Supervisor" },
  { id: 1, name: "Lecturer" },
  { id: 2, name: "Professional" },
];

const ReferenceFormStep = () => {
  const user = useAtomValue(userAtom);
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);

  const queryClient = useQueryClient();
  const { notification } = App.useApp();

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ["get-references"],
    enabled: !!user?.applicantId,
    queryFn: async () => {
      try {
        return await getReferenceByApplicantId(user?.applicantId ?? "");
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404 || status === 400) {
          return { data: { records: [], pageNumber: 1, pageSize: 0, totalRecords: 0, totalPages: 0 }, statusCode: 200, message: "" } as unknown as ReferenceResponse;
        }
        throw err;
      }
    },
  });

  const titleQuery = useQuery({
    queryKey: ["get-all-title"],
    queryFn: getAllTitle,
  });

  const referenceData = useMemo(() => {
    const records = data?.data?.records;
    return Array.isArray(records) ? records : [];
  }, [data?.data?.records]);

  const titleData = useMemo(() => {
    return titleQuery?.data?.data ?? [];
  }, [titleQuery?.data?.data]);

  const [formVisible, setFormVisible] = useState(referenceData?.length === 0);

  useEffect(() => {
    if (!isFetching && referenceData?.length === 0) {
      setFormVisible(true);
    }
  }, [isFetching, referenceData]);

  const createReferenceMutation = useMutation({
    mutationFn: createReference,
  });

  const deleteReferenceMutation = useMutation({
    mutationFn: deleteReference,
  });

  const handleCreateReference = async (values: ReferenceFormValues) => {
    const payload = {
      titleId: Number(values.titleId),
      applicantId: user?.applicantId ?? "",
      refereeName: values.refereeName,
      refereeEmail: values.refereeEmail,
      refereeCategory: Number(values.refereeCategory),
    };

    try {
      const result = await createReferenceMutation.mutateAsync(payload);
      notification.success({
        message: "Success",
        description: result?.message,
      });
      await queryClient.refetchQueries({ queryKey: ["get-references"] });
      setFormVisible(false);
    } catch (err: any) {
      const message: string = err?.response?.data?.message ?? "";
      const alreadyExists = message.toLowerCase().includes("already exists");

      if (alreadyExists) {
        notification.info({
          message: "Already saved",
          description: "This referee is already added to your application.",
        });
        await queryClient.refetchQueries({ queryKey: ["get-references"] });
        setFormVisible(false);
      } else {
        notification.error({
          message: "Error",
          description: message,
        });
      }
    }
  };

  const handleDeleteReference = async (id: number) => {
    try {
      const result = await deleteReferenceMutation.mutateAsync(id);
      notification.success({
        message: "Success",
        description: result?.message,
      });
      await queryClient.refetchQueries({ queryKey: ["get-references"] });
    } catch (err: any) {
      notification.error({
        message: "Error",
        description: err?.response?.data?.message,
      });
    }
  };

  const handleAddMore = () => {
    setFormVisible(true);
  };

  const handlePrevState = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const handleNextClick = () => {
    if (referenceData?.length === 1) {
      notification.error({
        message: "Error",
        description: "Please add at least 2 reference to proceed",
      });
    } else {
      setPageIndex(pageIndex + 1);
    }
  };

  if (isLoading || titleQuery.isLoading) {
    return <Spin size="large" />;
  }

  if (isError) {
    return <div>{(error as any)?.message}</div>;
  }

  return (
    <>
      <section className={styles.section}>
        <h5>Reference Form</h5>
        <p>All fields marked with a (*) are required. Please provide at least 2 referee.</p>
      </section>

      <hr />
      <div className={styles.docSection}>
        {referenceData?.map((item, index) => (
          <div key={item?.id}>
            <section className="space-between">
              <div>
                <h5>{item?.title} {item?.refereeName}</h5>
                <p className={styles.date}>
                  {item?.refereeEmail} •{" "}
                  {REFEREE_CATEGORIES.find((c) => c.id === item?.refereeCategory)?.name}
                </p>
              </div>

              <div className="small-gap">
                <button
                  type="button"
                  className={styles.transparentBtn}
                  onClick={() => handleDeleteReference(item?.id!)}
                >
                  <TrashIcon />
                </button>
              </div>
            </section>
            {index + 1 < referenceData.length && <hr />}
          </div>
        ))}

        {referenceData?.length > 0 && <hr />}

        {formVisible && (
          <ReferenceForm
            buttonText="Save"
            isLoading={createReferenceMutation?.isPending}
            titleOptions={titleData}
            onSubmit={handleCreateReference}
          />
        )}

        {!formVisible && referenceData?.length < 5 && (
          <button onClick={handleAddMore} className={styles.addQualification}>
            <div>
              <span>+</span>
            </div>
            <span>
              <b>Add Reference</b>
            </span>
          </button>
        )}

        <OnboardingButtonGroup
          onClick={handlePrevState}
          onNextClick={handleNextClick}
        />
      </div>
    </>
  );
};

export default ReferenceFormStep;